/**
 * Circuit Breaker Pattern Implementation
 *
 * ISSUE_045: 연속 실패 시 시스템 보호
 *
 * 상태 전이:
 * - CLOSED → 정상 작동, 요청 통과
 * - OPEN → 장애 감지, 요청 차단 (빠른 실패)
 * - HALF_OPEN → 복구 테스트, 제한된 요청 허용
 *
 * @module core/resilience/CircuitBreaker
 */

import type { CircuitState, CircuitBreakerConfig } from './types';
import { DEFAULT_CIRCUIT_CONFIG } from './types';

/**
 * Circuit Breaker 이벤트
 */
export type CircuitEvent =
  | { type: 'state_changed'; from: CircuitState; to: CircuitState }
  | { type: 'failure_recorded'; count: number; threshold: number }
  | { type: 'success_recorded'; count: number }
  | { type: 'request_rejected'; reason: string };

export type CircuitEventHandler = (event: CircuitEvent) => void;

/**
 * Circuit Breaker 클래스
 *
 * @example
 * ```ts
 * const breaker = new CircuitBreaker('api', { failureThreshold: 5 });
 *
 * try {
 *   const result = await breaker.execute(() => fetchData());
 * } catch (error) {
 *   if (error.message.includes('Circuit is OPEN')) {
 *     // 서킷이 열려있어 요청 차단됨
 *   }
 * }
 * ```
 */
export class CircuitBreaker {
  private name: string;
  private config: CircuitBreakerConfig;
  private state: CircuitState = 'closed';
  private failureCount = 0;
  private successCount = 0;
  private lastFailureAt: number | null = null;
  private openedAt: number | null = null;
  private eventHandlers: CircuitEventHandler[] = [];

  constructor(name: string, config: Partial<CircuitBreakerConfig> = {}) {
    this.name = name;
    this.config = { ...DEFAULT_CIRCUIT_CONFIG, ...config };
  }

  /**
   * 현재 상태 조회
   */
  getState(): CircuitState {
    // OPEN 상태에서 resetTimeout이 지났으면 HALF_OPEN으로 전환
    if (this.state === 'open' && this.openedAt) {
      const timeSinceOpen = Date.now() - this.openedAt;
      if (timeSinceOpen >= this.config.resetTimeout) {
        this.transitionTo('half-open');
      }
    }
    return this.state;
  }

  /**
   * 상태 정보 전체 조회
   */
  getStatus() {
    return {
      name: this.name,
      state: this.getState(),
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureAt: this.lastFailureAt,
      openedAt: this.openedAt,
      config: this.config,
    };
  }

  /**
   * 서킷이 열려있는지 확인
   */
  isOpen(): boolean {
    return this.getState() === 'open';
  }

  /**
   * 서킷이 닫혀있는지 확인 (정상)
   */
  isClosed(): boolean {
    return this.getState() === 'closed';
  }

  /**
   * 이벤트 핸들러 등록
   */
  onEvent(handler: CircuitEventHandler): () => void {
    this.eventHandlers.push(handler);
    return () => {
      this.eventHandlers = this.eventHandlers.filter(h => h !== handler);
    };
  }

  /**
   * 작업 실행
   * 서킷이 열려있으면 즉시 에러 반환 (빠른 실패)
   */
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    const currentState = this.getState();

    // OPEN 상태면 요청 차단
    if (currentState === 'open') {
      this.emit({ type: 'request_rejected', reason: 'Circuit is OPEN' });
      throw new Error(`[CircuitBreaker:${this.name}] Circuit is OPEN - request rejected`);
    }

    try {
      const result = await operation();
      this.recordSuccess();
      return result;
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }

  /**
   * 성공 기록
   */
  recordSuccess(): void {
    this.successCount++;
    this.emit({ type: 'success_recorded', count: this.successCount });

    if (this.state === 'half-open') {
      // HALF_OPEN에서 성공하면 CLOSED로
      if (this.successCount >= this.config.successThreshold) {
        this.reset();
      }
    } else if (this.state === 'closed') {
      // CLOSED에서 성공하면 실패 카운터 초기화
      this.failureCount = 0;
    }
  }

  /**
   * 실패 기록
   */
  recordFailure(): void {
    this.failureCount++;
    this.lastFailureAt = Date.now();
    this.emit({
      type: 'failure_recorded',
      count: this.failureCount,
      threshold: this.config.failureThreshold,
    });

    if (this.state === 'half-open') {
      // HALF_OPEN에서 실패하면 다시 OPEN
      this.trip();
    } else if (this.state === 'closed') {
      // CLOSED에서 임계값 도달하면 OPEN
      if (this.failureCount >= this.config.failureThreshold) {
        this.trip();
      }
    }
  }

  /**
   * 서킷 열기 (요청 차단 시작)
   */
  trip(): void {
    this.transitionTo('open');
    this.openedAt = Date.now();
    this.successCount = 0;
  }

  /**
   * 서킷 리셋 (정상 복구)
   */
  reset(): void {
    this.transitionTo('closed');
    this.failureCount = 0;
    this.successCount = 0;
    this.openedAt = null;
  }

  /**
   * 강제로 HALF_OPEN 상태로 전환 (수동 복구 시도)
   */
  halfOpen(): void {
    this.transitionTo('half-open');
    this.successCount = 0;
  }

  /**
   * 상태 전이
   */
  private transitionTo(newState: CircuitState): void {
    if (this.state === newState) return;

    const from = this.state;
    this.state = newState;

    this.emit({ type: 'state_changed', from, to: newState });
  }

  /**
   * 이벤트 발생
   */
  private emit(event: CircuitEvent): void {
    this.eventHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (err) {
        console.error(`[CircuitBreaker:${this.name}] Event handler error:`, err);
      }
    });
  }
}

/**
 * 싱글톤 서킷 브레이커 관리자
 */
class CircuitBreakerManager {
  private breakers = new Map<string, CircuitBreaker>();

  /**
   * 서킷 브레이커 가져오기 (없으면 생성)
   */
  get(name: string, config?: Partial<CircuitBreakerConfig>): CircuitBreaker {
    let breaker = this.breakers.get(name);

    if (!breaker) {
      breaker = new CircuitBreaker(name, config);
      this.breakers.set(name, breaker);
    }

    return breaker;
  }

  /**
   * 모든 서킷 브레이커 상태 조회
   */
  getAllStatus() {
    const status: Record<string, ReturnType<CircuitBreaker['getStatus']>> = {};
    this.breakers.forEach((breaker, name) => {
      status[name] = breaker.getStatus();
    });
    return status;
  }

  /**
   * 모든 서킷 브레이커 리셋
   */
  resetAll(): void {
    this.breakers.forEach(breaker => breaker.reset());
  }
}

/**
 * 전역 서킷 브레이커 관리자
 */
export const circuitBreakerManager = new CircuitBreakerManager();

/**
 * 편의 함수: 이름으로 서킷 브레이커 가져오기
 */
export function getCircuitBreaker(
  name: string,
  config?: Partial<CircuitBreakerConfig>
): CircuitBreaker {
  return circuitBreakerManager.get(name, config);
}
